import type { CampaignBlock } from '@goldfishcode/kingmakerdata-api-sdk/libs/api/campaign/models';
import type { DashboardFilterParams } from '@goldfishcode/kingmakerdata-api-sdk/libs/api/dashboard/models';
import type { User } from '@goldfishcode/kingmakerdata-api-sdk/libs/api/user/models';
import { compact, isEmpty, split } from 'lodash';
import { filterParamInitialize } from 'src/reducers/dashboard/const';

import { CityCouncilType } from './const';

export const isPrecinct = (profile: User) => {
  const councilTypes: string[] = [
    CityCouncilType.COUNTY_COMMISSIONER,
    CityCouncilType.SCHOOL_DISTRICT,
    CityCouncilType.CITY_COUNCIL,
    CityCouncilType.COUNTY_WIDE,
    CityCouncilType.CITY_WIDE,
  ];

  return (
    profile.race &&
    ((CityCouncilType.STATE_HOUSE === profile.race.title && !profile.is_have_more_one_county) ||
      councilTypes.includes(profile.race.title))
  );
};

export const getParams = (
  profile: User,
  currentParams: CampaignBlock | DashboardFilterParams,
  ignoreKeys: string[],
) => {
  let params: string[] = [];
  // Selection control default
  const controlPartiesDefault = filterParamInitialize.select_control_party?.split(',') ?? [];
  const controlPredictionsDefault = filterParamInitialize.select_control_vote_prediction?.split(',') ?? [];
  // Selection control of blockwalk or current filter params
  const controlPartiesSelected = currentParams.select_control_party?.split(',') ?? [];
  const controlPredictionsSelected = currentParams.select_control_vote_prediction?.split(',') ?? [];
  // Est parties and vote predictions of blockwalk or current filter params
  const estPartiesSelected = currentParams.est_party?.split(',') ?? [];
  const votePredictionsSelected = currentParams.voter_likelihood_bar?.split(',') ?? [];

  // Check if selection control is default
  const isEstPartiesDefault =
    controlPartiesDefault.filter(item => controlPartiesSelected.includes(item)).length === controlPartiesDefault.length;
  const isVotePredictionsDefault =
    controlPredictionsDefault.filter(item => controlPredictionsSelected.includes(item)).length ===
    controlPredictionsDefault.length;
  // Check if Est Parties and Vote Predictions are included in selection control
  const estPartiesIncluded = controlPartiesSelected.filter(item => estPartiesSelected.includes(item));
  const votePredictionsIncluded = controlPredictionsSelected.filter(item => votePredictionsSelected.includes(item));

  // If Est parties is included in selection control or isn't changed, ignore Control Party
  if (isEstPartiesDefault || (!isEstPartiesDefault && estPartiesIncluded.length > 0))
    ignoreKeys.push('select_control_party');
  // If Vote Predictions is included in selection control or isn't changed, ignore Control Vote Prediction
  if (isVotePredictionsDefault || (!isVotePredictionsDefault && votePredictionsIncluded.length > 0))
    ignoreKeys.push('select_control_vote_prediction');

  if (estPartiesIncluded.length > 0) {
    ignoreKeys.push('est_party');
    params.push(...estPartiesIncluded.filter(item => item));
  }
  if (votePredictionsIncluded.length > 0) {
    ignoreKeys.push('voter_likelihood_bar');
    params.push(...votePredictionsIncluded.filter(item => item));
  }

  if (currentParams.city_council) {
    ignoreKeys.push('precinct_dropdown');
  }

  if (currentParams.county_dropdown) {
    ignoreKeys.push('select_control_county_dropdown');
  }

  if (currentParams.precinct_dropdown) {
    ignoreKeys.push('select_control_precinct_dropdown');
  }

  Object.keys(currentParams).forEach(key => {
    if (currentParams[key] && !ignoreKeys.includes(key)) {
      const values = currentParams[key] as string;
      let array = typeof values === 'string' ? values.split(',') : [];

      if (key === 'city_council_exclude' && values === '--All') array = [];

      switch (key) {
        case 'voted':
          if (array.includes('No')) {
            params.push('Not Voted');
          }

          if (array.includes('Yes')) {
            params.push('Voted');
          }
          break;

        case 'city_council_exclude':
          // eslint-disable-next-line no-case-declarations
          const value = isPrecinct(profile) ? 'Precinct Others' : 'N/A';
          params.push(value);
          break;

        default:
          params = params.concat(array);
          break;
      }
    }
  });

  return params;
};

export const ableLoadingDefault = (partyParam?: string, votePredictionParam?: string) => {
  const parties = compact(split(partyParam, ','));
  const votePredictions = compact(split(votePredictionParam, ','));

  return isEmpty(parties) || isEmpty(votePredictions);
};
